import axios from "axios";
import Vue from "vue";

const fetched = { };
function isFetched( nodeId, projectId )
{
	return ( fetched[nodeId] && fetched[nodeId][projectId] );
}

function setFetched( nodeId, projectId )
{
	if( !fetched[nodeId] )
		fetched[nodeId] = { };
	fetched[nodeId][projectId] = true;
}

// initial state
const state = {
	lookup: []
}

// getters
const getters = {
	collections : ( state ) => ( nodeId, projectId ) =>
	{
		if( !nodeId )
			throw new Error( "Must provide a node-id to list collections by." );
		if( !projectId )
			throw new Error( "Must provide a project-id of collections to retrieve." );
		//console.log( "LOOKUP", nodeId, state.lookup[nodeId] );
		if( state.lookup[nodeId] )
			return Object.values( state.lookup[nodeId][projectId] || { } );
		return [ ];
	},
	collection : ( state, getters ) => ( nodeId, projectId, collectionName ) =>
	{
		if( !nodeId )
			throw new Error( "Must provide a node-id to retrieve a project by." );
		if( !projectId )
			throw new Error( "Must provide a project-id of collection to retrieve." );
		if( !collectionName )
			throw new Error( "Must provide a collection-name to retrieve." );
		if( state.lookup[nodeId] && state.lookup[nodeId][projectId] )
			return state.lookup[nodeId][projectId][collectionName] || null;
		return null;
	}
}

// actions
const actions =
{
	async list( { state, getters, commit, dispatch, rootGetters }, { nodeId = null, projectId, options = { }, nocache = false } )
	{
		let homeNode = await dispatch( 'nodes/getHomeNode', { }, { root : true } );
		nodeId = nodeId || homeNode.$id;
		
		let collections;
		if( isFetched( nodeId, projectId ) && !nocache )
		{
			collections = getters.collections( nodeId, projectId );
			if( Array.isArray( collections ) && collections.length > 0 )
				return collections;
		}

		let proxyPrefix = "";
		if( nodeId != homeNode.$id )
			proxyPrefix = "/proxy/"+ nodeId;
		
		let sessionKey = rootGetters['authentication/activeSession']( nodeId, true );

		try {
			let result = await axios( { method: "GET", params: options, url: process.env.APIHOST + proxyPrefix +"/api/v1/projects/"+ projectId +"/collections", headers : { 'wn-session-key' : sessionKey } } );
			collections = result.data.$data;
			if( !Array.isArray( collections ) )
				console.error( "Data is NOT an array! This should never happen!" );
			commit( 'setCollections', { nodeId, projectId, collections } );
			setFetched( nodeId, projectId );
			return collections;
		} catch( err ) {
			throw err;
		}
	},
	async read( { state, getters, commit, dispatch, rootGetters }, { nodeId = null, projectId, collectionName, options = { }, nocache = false } )
	{
		let homeNode = await dispatch( 'nodes/getHomeNode', { }, { root : true } );
		nodeId = nodeId || homeNode.$id;
		
		let collection = null;

		if( !nocache )
		{
			collection = getters.collections( nodeId, projectId, collectionName, objectId );
			if( collection && typeof collection === 'object' )
				return collection;
		}

		let proxyPrefix = "";
		if( nodeId != homeNode.$id )
			proxyPrefix = "/proxy/"+ nodeId;
		
		let sessionKey = rootGetters['authentication/activeSession']( nodeId, true );

		try {
			let result = await axios( { method: "GET", params: options, url: process.env.APIHOST + proxyPrefix +"/api/v1/projects/"+ projectId +"/collections/"+ collectionName, headers : { 'wn-session-key' : sessionKey } } );
			collection = result.data.$data || null;
			if( collection )
				commit( 'setCollection', { nodeId, projectId, collection } );
			return collection;
		} catch( err ) {
			if( err.response && err.response.status == 404 )
			{
				commit( 'removeCollection', { nodeId, projectId, collectionName } );
				return null;
			}
			throw err;
		}
	},
	async create( { state, getters, commit, dispatch, rootGetters }, { nodeId, projectId, newCollection, options = { } } )
	{
		let homeNode = await dispatch( 'nodes/getHomeNode', { }, { root : true } );
		nodeId = nodeId || homeNode.$id;

		if( !newCollection.$id || projectId !== newCollection.$id.split(':')[0] )
			throw new Error( "Must provide a colleciton ID in the format <project-id>:<collection-name> for THIS project!" );

		let proxyPrefix = "";
		if( nodeId != homeNode.$id )
			proxyPrefix = "/proxy/"+ nodeId;
		
		let sessionKey = rootGetters['authentication/activeSession']( nodeId, true );

		try {
			newCollection = _.omit( newCollection, [ '$node' ] );	// Remove the $node, without modifying the original parameter!

			let result = await axios( { method: "POST", params: options, url: process.env.APIHOST + proxyPrefix +"/api/v1/projects/"+ projectId +"/collections", data: newCollection, headers : { 'wn-session-key' : sessionKey } } );
			let collection = result.data.$data;
			commit( 'setCollection', { nodeId, projectId, collection } );
			return collection;
		} catch( err ) {
			if( err.response && err.response.data && err.response.data.$error && err.response.data.$error.message )
				throw new Error( err.response.data.$error.message );
			throw err;
		}
	},
	async update( { state, getters, commit, dispatch, rootGetters }, { nodeId = null, projectId, collectionName, updates, options = { } } )
	{
		let homeNode = await dispatch( 'nodes/getHomeNode', { }, { root : true } );
		nodeId = nodeId || homeNode.$id;
		
		let proxyPrefix = "";
		if( nodeId != homeNode.$id )
			proxyPrefix = "/proxy/"+ nodeId;
		
		let sessionKey = rootGetters['authentication/activeSession']( nodeId, true );

		try {
			updates = _.omit( updates, [ '$id', '$node' ] );	// Remove the $id and $node, without modifying the original parameter!

			let result = await axios( { method: "PATCH", params: options, url: process.env.APIHOST + proxyPrefix +"/api/v1/projects/"+ projectId +"/collections/"+ collectionName, data: updates, headers : { 'wn-session-key' : sessionKey } } );
			let collection = result.data.$data;
			commit( 'setCollection', { nodeId, projectId, collection } );
			return collection;
		} catch( err ) {
			if( err.response && err.response.status == 404 )
			{
				commit( 'removeCollection', { nodeId, projectId, collectionName } );
				return null;
			}
			throw err;
		}
	},
	async delete( { state, getters, commit, dispatch, rootGetters }, { nodeId, projectId, collectionName, options = { } } )
	{
		let homeNode = await dispatch( 'nodes/getHomeNode', { }, { root : true } );
		nodeId = nodeId || homeNode.$id;

		let proxyPrefix = "";
		if( nodeId != homeNode.$id )
			proxyPrefix = "/proxy/"+ nodeId;
		
		let sessionKey = rootGetters['authentication/activeSession']( nodeId, true );

		try {
			let result = await axios( { method: "DELETE", params: options, url: process.env.APIHOST + proxyPrefix +"/api/v1/projects/"+ projectId +"/collections/"+ collectionName, headers : { 'wn-session-key' : sessionKey } } );
			commit( 'removeCollection', { nodeId, projectId, collectionName } );
			return true;
		} catch( err ) {
			if( err.response && err.response.status == 404 )
			{
				commit( 'removeCollection', { nodeId, projectId, collectionName } );
				return false;
			}
			throw err;
		}
	},
}

// mutations
const mutations =
{
	setCollections( state, { nodeId, projectId, collections } )
	{
		if( !nodeId || typeof nodeId !== 'string' )
			throw new TypeError( "You must provide a node ID!" );
		if( !projectId || typeof projectId !== 'string' )
			throw new TypeError( "You must provide a project ID!" );
		if( !Array.isArray( collections ) )
			throw new TypeError( "You must provide an array of collections!" );
		if( !state.lookup[nodeId] )
			Vue.set( state.lookup, nodeId, { } );
		Vue.set( state.lookup[nodeId], projectId, { } );	// Reset collections first.
		for( let i=0; i < collections.length; i++ )
		{
			let collection = collections[i];
			collection.$node = nodeId;
			let [ colProject, colName = "" ] = collection.$id.split( ":" );
			if( projectId != colProject )
				throw new Error( "The collection given has a different project ["+ colProject +"] than the project for which we're setting collections ["+ projectId +"]" );
			if( !colName )
				throw new Error( "No collection name found! Proper collection ID format is '<projId>:<colName>', we got '"+ collection.$id +"'." );
			Vue.set( state.lookup[nodeId][projectId], colName, collection );
		}
		console.log( "SET COLLECTIONS", state.lookup[nodeId][projectId] );
	},
	setCollection( state, { nodeId, projectId, collection } )
	{
		if( !nodeId || typeof nodeId !== 'string' )
			throw new Error( "You must provide a nodeID!" );
		if( !projectId || typeof projectId !== 'string' )
			throw new TypeError( "You must provide a project ID!" );
		if( !collection || typeof collection !== 'object' || !collection.$id )
			throw new Error( "You must provide a collection definition with an ID!" );
		
		collection.$node = nodeId;

		if( !state.lookup[nodeId] )
			Vue.set( state.lookup, nodeId, { } );
		if( !state.lookup[nodeId][projectId] )
			Vue.set( state.lookup[nodeId], projectId, { } );
		
		Vue.set( state.lookup[nodeId][projectId], collection.$id.split(':')[1], collection );
	},
	removeCollection( state, { nodeId, projectId, collectionName } )
	{
		if( state.lookup[nodeId] && state.lookup[nodeId][projectId] )
			Vue.delete( state.lookup[nodeId][projectId], collectionName );
	}
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations
}


// WEBPACK FOOTER //
// ./src/store/modules/collections.js